import { useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';
import { mascaraDaRolagem, useRolagemHorizontal } from '../core/rolagem';
import { Checkbox } from '../forms/Checkbox';

/** Valor aceito como identificador de linha. */
export type RowId = string | number;

/**
 * Qualquer objeto serve de linha. Não é `any`: as células continuam tipadas
 * pelo `Row` concreto de quem usa a tabela.
 *
 * Era `Record<string, unknown>`, o que **excluía toda interface**: o TypeScript
 * não dá assinatura de índice implícita a `interface`, só a `type`. Na prática a
 * tabela recusava justamente os tipos do domínio — `Paciente`, `Lead` — e
 * obrigava a espalhar `as` por quem a usa.
 */
export type DataTableRow = object;

/** Leitura por nome de campo, no único ponto onde a tabela precisa dela. */
function campo<Row extends DataTableRow>(row: Row, chave: string): unknown {
    return (row as Record<string, unknown>)[chave];
}

export interface DataTableColumn<Row extends DataTableRow> {
    /** Campo do registro. Também é a `key` da célula. */
    key: string;
    header?: ReactNode;
    align?: 'left' | 'center' | 'right';
    width?: number | string;
    sortable?: boolean;
    /** Conteúdo customizado. Sem ele, exibe `row[key]`. */
    render?: (row: Row) => ReactNode;
}

export interface DataTableProps<Row extends DataTableRow> {
    columns?: DataTableColumn<Row>[];
    rows?: Row[];
    selectable?: boolean;
    selected?: RowId[];
    onSelect?: (ids: RowId[]) => void;
    onRowClick?: (row: Row) => void;
    /** Campo que identifica a linha. Cai no índice quando o campo falta. */
    rowKey?: string;
    emptyMessage?: ReactNode;
    stickyHeader?: boolean;
    /**
     * Nome acessível da linha clicável. Sem isto, o nome sai da primeira coluna
     * textual — e quando essa coluna é uma data ISO ou um código, quem ouve a
     * tabela recebe "Abrir 2026-09-08T08:00:00-03:00" em vez do paciente.
     */
    rowLabel?: (row: Row) => string;
    style?: CSSProperties;
}

/** Controles internos da linha. Clique ou Enter neles é ação deles, não da linha. */
const CONTROLES_INTERNOS = 'button, a, input, select, textarea, label, [role="menu"], [data-parar-linha]';

function ehId(v: unknown): v is RowId {
    return typeof v === 'string' || typeof v === 'number';
}

/* Nome acessível da linha: usa a primeira coluna textual, porque "Abrir
   registro" repetido 25 vezes não distingue nada para quem ouve a tabela. */
function rotuloLinha<Row extends DataTableRow>(row: Row, id: RowId, columns: DataTableColumn<Row>[], rowLabel?: (row: Row) => string): string {
    if (rowLabel) return `Abrir ${rowLabel(row)}`;
    const primeira = columns.find((c) => {
        const valor = campo(row, c.key);
        return typeof valor === 'string' && valor.trim().length > 0;
    });
    return primeira ? `Abrir ${String(campo(row, primeira.key))}` : `Abrir registro ${id}`;
}

export function DataTable<Row extends DataTableRow>({
    columns = [], rows = [], selectable = false, selected = [], onSelect, onRowClick,
    rowKey = 'id', emptyMessage = 'Nenhum registro encontrado.', stickyHeader = true, rowLabel, style,
}: DataTableProps<Row>) {
    const [hoverRow, setHoverRow] = useState<RowId | null>(null);
    const rolavel = useRef<HTMLDivElement | null>(null);
    const rolagem = useRolagemHorizontal(rolavel, `${columns.length}:${rows.length}`);
    const todasMarcadas = rows.length > 0 && selected.length === rows.length;

    const alternar = (id: RowId) => {
        onSelect?.(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
    };

    const idDaLinha = (row: Row, indice: number): RowId => {
        const bruto = campo(row, rowKey);
        /* `key` precisa existir sempre: linha cuja fonte não traz o campo de
           `rowKey` gerava key undefined, e o React reconcilia errado — numa
           tabela editável isso troca o conteúdo entre linhas. O índice é o
           último recurso, não o padrão. */
        return ehId(bruto) ? bruto : `linha-${indice}`;
    };

    return (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)', overflow: 'hidden', minWidth: 0, maxWidth: '100%', ...style }}>
            {/* Tabela densa rola de lado no celular — nunca a página. A borda
                esmaecida diz que há mais colunas; sem ela, "Situação" terminava
                cortada ao meio parecendo o fim da tabela. */}
            <div ref={rolavel} data-rolagem="horizontal" style={{ overflowX: 'auto', ...mascaraDaRolagem(rolagem, 40) }}>
                <table className="vec-rows" style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: 'var(--surface-secondary)', position: stickyHeader ? 'sticky' : undefined, top: 0, zIndex: 1 }}>
                            {selectable && (
                                <th style={{ width: 40, padding: '0 0 0 14px', height: 38, borderBottom: '1px solid var(--border)' }}>
                                    <Checkbox
                                        checked={todasMarcadas}
                                        indeterminate={selected.length > 0 && !todasMarcadas}
                                        style={{ marginLeft: 0 }}
                                        /* Caixa sem rótulo visível precisa de nome: sem ele o
                                           leitor de tela anuncia "caixa de seleção" e nada mais —
                                           e numa tabela são uma por linha, todas iguais. */
                                        ariaLabel={todasMarcadas ? 'Desmarcar todas as linhas' : 'Marcar todas as linhas'}
                                        onChange={() => onSelect?.(todasMarcadas ? [] : rows.map((r, i) => idDaLinha(r, i)))}
                                    />
                                </th>
                            )}
                            {columns.map((c) => (
                                <th
                                    key={c.key}
                                    scope="col"
                                    style={{
                                        textAlign: c.align ?? 'left', padding: '0 14px', height: 38, whiteSpace: 'nowrap',
                                        fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-snug)', color: 'var(--text-secondary)',
                                        borderBottom: '1px solid var(--border)', width: c.width,
                                    }}
                                >
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: c.sortable ? 'pointer' : 'default' }}>
                                        {c.header}
                                        {c.sortable && <Icon name="chevrons-up-down" size={12} style={{ color: 'var(--neutral-400)' }} />}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={columns.length + (selectable ? 1 : 0)}
                                    style={{ padding: '36px 14px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13.5 }}
                                >
                                    {emptyMessage}
                                </td>
                            </tr>
                        )}
                        {rows.map((row, indice) => {
                            const id = idDaLinha(row, indice);
                            const marcada = selected.includes(id);
                            return (
                                /* Linha clicável, alcançável por teclado — SEM role="button".
                                   `role` no <tr> tira a linha da árvore da tabela e o leitor de
                                   tela perde linha e coluna: some justamente a estrutura que
                                   torna a tabela legível. Mantém-se a semântica nativa e
                                   acrescenta-se tabIndex, Enter/Espaço e nome acessível.

                                   Com teclado e nome acessível providos, o jsx-a11y aceita o
                                   conjunto sem exceção: nenhuma regra precisou ser desligada. */
                                <tr
                                    key={id}
                                    onMouseEnter={() => setHoverRow(id)}
                                    onMouseLeave={() => setHoverRow(null)}
                                    onClick={onRowClick ? (e) => {
                                        /* `disparar` ignora o evento que nasceu num controle
                                           interno: sem isso, clicar no lápis da linha abriria
                                           o editor E o detalhe da linha. */
                                        if ((e.target as HTMLElement).closest(CONTROLES_INTERNOS)) return;
                                        onRowClick(row);
                                    } : undefined}
                                    onKeyDown={onRowClick ? (e) => {
                                        /* Só a própria linha responde: Enter num botão dentro
                                           dela é ação do botão, e disparar os dois é execução
                                           dupla. */
                                        if (e.target !== e.currentTarget) return;
                                        if (e.key !== 'Enter' && e.key !== ' ') return;
                                        e.preventDefault();
                                        onRowClick(row);
                                    } : undefined}
                                    tabIndex={onRowClick ? 0 : undefined}
                                    aria-label={onRowClick ? rotuloLinha(row, id, columns, rowLabel) : undefined}
                                    aria-selected={selectable ? marcada : undefined}
                                    style={{
                                        background: marcada ? 'var(--surface-brand-soft)' : hoverRow === id ? 'var(--surface-secondary)' : 'transparent',
                                        cursor: onRowClick ? 'pointer' : 'default',
                                        transition: 'background-color var(--dur-fast) var(--ease-standard)',
                                    }}
                                >
                                    {selectable && (
                                        /* Sem `stopPropagation`: o clique da linha já ignora o
                                           que nasce em `label`/`input`, e um manipulador de
                                           clique numa <td> seria controle improvisado. */
                                        <td style={{ padding: '0 0 0 14px', height: 44, borderBottom: '1px solid var(--border-subtle)' }}>
                                            <Checkbox
                                                checked={marcada} onChange={() => alternar(id)}
                                                ariaLabel={`${marcada ? 'Desmarcar' : 'Marcar'} ${rotuloLinha(row, id, columns, rowLabel)}`}
                                            />
                                        </td>
                                    )}
                                    {columns.map((c) => (
                                        <td
                                            key={c.key}
                                            style={{
                                                padding: '0 14px', height: 44, textAlign: c.align ?? 'left',
                                                borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)',
                                                fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : undefined, whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {c.render ? c.render(row) : (campo(row, c.key) as ReactNode)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
